import React from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';
import { surveyTheme as theme } from './surveyTheme';

/**
 * ChoiceButton - Survey answer option
 *
 * Design principles:
 * - All choices visually identical (no priming)
 * - Clear selected/unselected states
 * - Large touch targets for mobile
 * - Fast, subtle transitions
 */
const ChoiceButton = ({ choice, isSelected, onSelect, lang = 'en' }) => {
  const choiceText = (lang === 'es' && choice.text_es) ? choice.text_es : choice.text;
  return (
    <Box
      as="button"
      type="button"
      onClick={() => onSelect(choice.id)}
      width="100%"
      textAlign="left"
      bg={isSelected ? theme.colors.teal : theme.colors.surface}
      color={isSelected ? 'white' : theme.colors.textSecondary}
      border="1.5px solid"
      borderColor={isSelected ? theme.colors.teal : theme.colors.borderLight}
      borderRadius={theme.radii.button}
      padding={theme.spacing.buttonPadding}
      cursor="pointer"
      transition={`background-color ${theme.transitions.normal}, border-color ${theme.transitions.normal}, color ${theme.transitions.normal}, box-shadow ${theme.transitions.normal}`}
      boxShadow={isSelected ? theme.shadows.selected : 'none'}
      _hover={{
        borderColor: isSelected ? theme.colors.teal : theme.colors.teal,
        boxShadow: isSelected ? theme.shadows.selected : theme.shadows.subtle,
      }}
      _focus={{
        outline: 'none',
        boxShadow: `0 0 0 3px rgba(59, 107, 107, 0.3)`,
      }}
      _active={{
        transform: 'scale(0.99)',
      }}
      role="radio"
      aria-checked={isSelected}
    >
      <Flex align="center" justify="space-between" gap={3}>
        <Text
          fontSize={theme.typography.choice.fontSize}
          fontWeight={isSelected ? 500 : theme.typography.choice.fontWeight}
          lineHeight={theme.typography.choice.lineHeight}
          fontFamily={theme.typography.fontFamily}
        >
          {choiceText}
        </Text>

        {/* Checkmark - only visible when selected */}
        <Box
          opacity={isSelected ? 1 : 0}
          transform={isSelected ? 'scale(1)' : 'scale(0.8)'}
          transition={`opacity ${theme.transitions.fast}, transform ${theme.transitions.fast}`}
          flexShrink={0}
        >
          <CheckIcon boxSize={4} />
        </Box>
      </Flex>
    </Box>
  );
};

export default ChoiceButton;
